import React, { FC, useEffect, useState } from 'react';
import { Loader, Sidebar } from 'components/common';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import { useTranslation } from 'react-i18next';
import {
  changestatusNote,
  deleteNote,
  exportExpensesResponse,
  // exportExpensesResponse,
  GetDetailsExpense,
  GetExpenseData,
  // GetExpenseData,
  supportChangeStatusNote,
} from 'features/expenses/services/Expenses';
import { hasPermission } from 'utils/permissions';
import { useAuthContext } from 'contexts/AuthContext';
import '@fortawesome/fontawesome-free/css/all.min.css';
import CategoryExpense from './CategoryExpense';
import Timeline from './TimelineExpenses';
import CategoryDetails from './CategoryDetails';
import { toast } from 'react-toastify';
import { useDialog } from 'contexts/DialogContext';
import CreateExpenseForm from './CreateExpenseForm';
import { getClassStatus, getStatusText, statusMap } from './ExpensesComponents';
import UserShortCut from 'components/common/UserShortCut';
import {
  ExpenseDetail,
  NoteDetailResponse,
  NoteStatus,
} from 'features/expenses/types/type';
import moment from 'moment';
import ExpensesCanceled from './ExpensesCanceled';
import ExpensesRefund from './ExpensesRefund';
import { useMediaContext } from 'contexts/MediaContext';
import Tippy from '@tippyjs/react';
import NewExpense from './NewExpense';
import { downloadFromArrayBuffer } from 'utils/globalMethodes';
import { generateExpenseHTML } from './DetailExpensesTemplate';
import {
  MintHrDropdown,
  MintHrDropdownItem,
} from 'components/common/MintHrDropdown';
import styles from './ExpenseDetails.module.css';

interface ExpenseDetailsProps {
  noteId: number | undefined;
  toggleRefreshDT?: () => void;
  statsRefetch?: () => void;
}

const supportStatusOptions: NoteStatus[] = [
  NoteStatus.Draft,
  NoteStatus.Pending,
  NoteStatus.Success,
  NoteStatus.Refund,
  NoteStatus.Rejected,
  NoteStatus.Canceled,
];

const ExpenseDetails: FC<SideBarProps<ExpenseDetailsProps>> = ({
  sidebar,
  setSidebarWidth,
  sidebarWidth,
  params,
}) => {
  const { t } = useTranslation();
  const { openSidebar, closeSidebar, closeMultipleSidebars } =
    useSidebarManager();
  const { openMedia } = useMediaContext();
  const [model, setModel] = useState<NoteDetailResponse>();
  const { user } = useAuthContext();
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);
  const dialog = useDialog();
  const [refresh, setRefresh] = useState(false);
  const userId = user?.userId === model?.affectedUserId;
  const isSupportRole = user?.role === 'SupportMintHR';
  const [isSupportStatusChanging, setIsSupportStatusChanging] = useState(false);

  const toggleRefresh = (): void => setRefresh((prev) => !prev);

  const toggleHistoryVisibility = (): void => {
    setIsHistoryVisible((prev) => !prev);
  };

  const fetchExpenseDetails = async (expenseId: number): Promise<void> => {
    const response = await GetDetailsExpense(expenseId);
    if (response.success) {
      setModel(response.data);
    }
  };

  useEffect(() => {
    if (params.noteId) {
      fetchExpenseDetails(params.noteId);
    }
  }, [params.noteId, refresh]);

  const handleStatusChange = async (
    status: number,
    refundedBy?: string,
    comment?: string,
  ): Promise<void> => {
    const response = await changestatusNote({
      id: model?.id ?? 0,
      refundedBy: refundedBy ?? undefined,
      comment: comment ?? undefined,
      status: status,
    });
    if (response.success) {
      toggleRefresh();
      params?.toggleRefreshDT?.();
      params?.statsRefetch?.();
      toast.success(t('Default.succesMessage'));
    } else {
      // e.g. Expenses.Attachment.Required.Note: an expense lacks its required attachment.
      toast.error(t(response?.message));
    }
  };

  const handleSupportStatusChange = async (
    status: NoteStatus,
  ): Promise<void> => {
    if (!model || model.status === status || isSupportStatusChanging) return;

    setIsSupportStatusChanging(true);
    try {
      const response = await supportChangeStatusNote({
        id: model.id,
        status,
      });

      if (response.success) {
        toggleRefresh();
        params?.toggleRefreshDT?.();
        params?.statsRefetch?.();
        toast.success(t('Default.succesMessage'));
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error('Error changing support expense status:', error);
    } finally {
      setIsSupportStatusChanging(false);
    }
  };

  const renderStatusBadge = (status: number, className = ''): JSX.Element => {
    const statusInfo = statusMap[status];

    return (
      <div
        className={`minthr-badge ${statusInfo?.className ?? ''} minthr-badge-s minthr-px-2 ${className}`}
      >
        {statusInfo?.label ? t(statusInfo.label) : ''}
      </div>
    );
  };

  const getStatusLabel = (status: number): string => {
    const statusInfo = statusMap[status];
    return statusInfo?.label ? t(statusInfo.label) : '';
  };

  const renderSupportStatusTrigger = (status: number): JSX.Element => {
    const statusInfo = statusMap[status];

    return (
      <button
        type="button"
        className={`dropdown-toggle ${styles.expenseStatusSelectTrigger}`}
        disabled={isSupportStatusChanging}
        aria-label={t('status')}
      >
        <span className={styles.expenseStatusSelectValue}>
          <span
            className={`minthr-badge ${statusInfo?.className ?? ''} minthr-badge-s ${styles.expenseStatusSelectCurrentBadge}`}
          >
            {getStatusLabel(status)}
          </span>
        </span>
        <span className={styles.expenseStatusSelectArrow}>
          {isSupportStatusChanging ? (
            <span className={styles.expenseStatusSelectSpinner} />
          ) : (
            <img src="/icons/arrow-down.svg" alt="" />
          )}
        </span>
      </button>
    );
  };

  const renderSupportStatusOption = (
    status: NoteStatus,
    isCurrent: boolean,
  ): JSX.Element => (
    <span className={styles.expenseStatusSelectOption}>
      <span className={styles.expenseStatusSelectOptionLabel}>
        {getStatusLabel(status)}
      </span>
      <span className={styles.expenseStatusSelectCheck}>
        {isCurrent && <img src="/icons/check.svg" alt="" />}
      </span>
    </span>
  );

  const handleDelete = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!model) return;
    const confirmed = await dialog.delete(
      'Expenses.Delete.Title',
      'Expenses.Delete.desc',
    );

    if (confirmed) {
      const response = await deleteNote(model?.id);
      if (response.success) {
        closeSidebar(ExpenseDetails);
        params?.toggleRefreshDT?.();
        params?.statsRefetch?.();
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    }
  };

  const handleEdit = (): void => {
    if (!model) return;
    openSidebar(CreateExpenseForm, true, {
      model: model,
      toggleRefresh: toggleRefresh,
      toggleRefreshDT: params.toggleRefreshDT,
    });
  };

  const handleOpenSidebar = (expenseId: number, catId: number): void => {
    closeMultipleSidebars([NewExpense as React.FC, CategoryExpense]);

    const sidebarData = {
      noteId: params.noteId ?? 0,
      expenseId: expenseId,
      catId: catId,
      toggleRefresh: toggleRefresh,
      toggleRefreshDT: params.toggleRefreshDT,
    };

    openSidebar(CategoryDetails, false, sidebarData);
  };

  const getFileIcon = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf':
        return '/icons/doc-pdf.svg';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '/icons/doc-img.svg';
      case 'doc':
      case 'docx':
        return '/icons/doc-word.svg';
      case 'xls':
      case 'xlsx':
        return '/icons/doc-excel.svg';
      default:
        return '/icons/doc-file.svg';
    }
  };

  const isPreviewable = (fileName: string): boolean => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    return ext === 'pdf' || ['jpg', 'jpeg', 'png', 'gif'].includes(ext || '');
  };

  const shouldShowEditButton = (): boolean => {
    const hasEditPermission =
      (!userId && hasPermission('Expenses.Edit')) ||
      (userId &&
        hasPermission('Profile.Expenses.Edit') &&
        model?.status !== NoteStatus.Success &&
        model?.status !== NoteStatus.Refund);

    const isEditableStatus =
      model?.status === NoteStatus.Draft ||
      model?.status === NoteStatus.Canceled ||
      (model?.status === NoteStatus.Pending &&
        !(
          model?.userslistApproval?.some((x) => x.status === 'Approved') ??
          false
        ));

    return hasEditPermission && isEditableStatus;
  };

  const shouldShowDeleteButton = (): boolean => {
    const hasDeletePermission =
      (!userId && hasPermission('Expenses.Delete')) ||
      (userId &&
        hasPermission('Profile.Expenses.Delete') &&
        model?.status !== NoteStatus.Success &&
        model?.status !== NoteStatus.Refund);

    const isDeletableStatus =
      model?.status === NoteStatus.Draft ||
      (model?.status === NoteStatus.Pending &&
        !(
          model?.userslistApproval?.some((x) => x.status === 'Approved') ??
          false
        ));

    return hasDeletePermission && isDeletableStatus;
  };

  const shouldShowExportButton = (): boolean => {
    return userId || hasPermission('Expenses.ExportPdf');
  };

  const shouldShowNewExpenseButton = (): boolean => {
    return (
      model?.status === NoteStatus.Draft ||
      model?.status === NoteStatus.Canceled ||
      (model?.status === NoteStatus.Pending &&
        !(
          model?.userslistApproval?.some((x) => x.status === 'Approved') ??
          false
        ))
    );
  };

  const shouldShowStatusChangeButtons = (): boolean => {
    if (model?.status === NoteStatus.Refund) return false;

    const hasPermissionToChange =
      (userId &&
        hasPermission('Profile.Expenses.Edit') &&
        (model?.status === NoteStatus.Draft ||
          model?.status === NoteStatus.Canceled)) ||
      (hasPermission('Expenses.Edit') &&
        (model?.status === NoteStatus.Draft ||
          model?.status === NoteStatus.Canceled)) ||
      (model?.actionBy === null &&
        model?.status !== NoteStatus.Draft &&
        model?.status !== NoteStatus.Canceled);

    return hasPermissionToChange;
  };

  const handleDownloadPDF = async (): Promise<void> => {
    try {
      if (!model?.id) return;

      const response = await GetExpenseData(model.id);

      const htmlContent = generateExpenseHTML(response, {
        t: (key: string) => t(key),
        formatDate: (date: string) => moment(date).format('DD MMM YYYY HH:mm'),
        formatDateOnly: (date: string) => moment(date).format('DD MMM YYYY'),
      });

      const arrayBuffer = await exportExpensesResponse(
        htmlContent,
        `Expense_${model.title}_${model.id}`,
        model.id,
      );

      downloadFromArrayBuffer(
        arrayBuffer,
        `Expense_${model.title}_${model.id}.pdf`,
      );

      toast.success(t('PDF.Download.Success'));
    } catch (error) {
      console.error('Error downloading expense PDF:', error);
    }
  };

  if (!model) return null;

  return (
    <Sidebar
      size="m"
      type="primary"
      sidebar={sidebar}
      setSidebarWidth={setSidebarWidth}
      sidebarWidth={sidebarWidth}
    >
      {!model ? (
        <Loader />
      ) : (
        <>
          <div className="minthr-flex minthr-flex-items-center minthr-pb-2 minthr-mb-3 minthr-border-b minthr-border-b-3">
            <div className="minthr-icon-bg minthr-icon-bg-l minthr-icon-bg-secondary minthr-icon-circle minthr-mr-3">
              <img
                className="minthr-icon-m"
                src="/icons/wallet.svg"
                alt="wallet icon"
              />
            </div>
            <h3>{t('Notes.de.frais')}</h3>
            <button
              className="minthr-sidebar-close"
              type="button"
              onClick={() => closeSidebar(ExpenseDetails)}
            >
              <img src="/icons/close.svg" alt="close icon" />
            </button>
          </div>

          <div className="minthr-flex minthr-flex-items-center minthr-flex-space-between">
            <h3>{model?.title}</h3>

            {isSupportRole && (
              <div className="minthr-ml-auto minthr-mr-2">
                <MintHrDropdown
                  title={renderSupportStatusTrigger(model.status)}
                  className={styles.expenseStatusSelect}
                  OverlayPanelClassName={styles.expenseStatusSelectOverlay}
                  titleClassName="d-inline-block"
                >
                  {supportStatusOptions.map((status) => (
                    <MintHrDropdownItem
                      key={status}
                      className={`${styles.expenseStatusSelectItem} ${
                        model.status === status ? styles.isCurrent : ''
                      }`}
                      onClick={() => void handleSupportStatusChange(status)}
                    >
                      {renderSupportStatusOption(
                        status,
                        model.status === status,
                      )}
                    </MintHrDropdownItem>
                  ))}
                </MintHrDropdown>
              </div>
            )}

            {shouldShowEditButton() && (
              <button
                type="button"
                className={`minthr-btn minthr-pr-1 minthr-btn-icon-origin ${!isSupportRole ? 'minthr-ml-auto' : ''}`}
                onClick={handleEdit}
              >
                <img
                  className="minthr-icon-s"
                  src="/icons/edit.svg"
                  alt="Edit"
                />
              </button>
            )}

            {shouldShowDeleteButton() && (
              <button
                type="button"
                className="minthr-btn minthr-pl-0"
                onClick={handleDelete}
              >
                <img
                  className="minthr-icon-danger minthr-icon-s"
                  src="/icons/delete.svg"
                  alt="Delete"
                />
              </button>
            )}

            {shouldShowExportButton() && (
              <button
                type="button"
                className="minthr-btn minthr-pl-1 minthr-btn-icon-origin"
                onClick={handleDownloadPDF}
                title={t('Download.PDF')}
              >
                <img
                  className="minthr-icon-s"
                  src="/icons/download.svg"
                  alt="Download PDF"
                />
              </button>
            )}
          </div>

          <div className="minthr-pt-2">
            <div className="row g-0 minthr-flex-items-center minthr-mb-1">
              <div className="col-sm-4">
                <span className="minthr-text-uppercase p">{t('status')}</span>
              </div>
              <div className="col-sm-8">{renderStatusBadge(model.status)}</div>
            </div>

            <div className="row g-0 minthr-flex-items-center minthr-mb-1">
              <div className="col-sm-4">
                <span className="minthr-text-uppercase p">{t('date')}</span>
              </div>
              <div className="col-sm-8">
                <div className="minthr-color-dark h5">
                  {model?.executionDate &&
                    moment(model?.executionDate).format('DD MMM yyyy')}
                </div>
              </div>
            </div>

            <div className="row g-0 minthr-flex-items-center minthr-mb-1">
              <div className="col-sm-4">
                <span className="minthr-text-uppercase p">
                  {t('Note.Affectedto')}
                </span>
              </div>
              <div className="col-sm-8">
                <div className="minthr-flex minthr-flex-items-center minthr-color-dark h5">
                  {model?.userImage?.fullName}
                  {model?.userImage?.id && (
                    <UserShortCut
                      src={`upload/profileimages/${model?.userImage.id}/${model?.userImage.picturePath}`}
                      alt={model?.userImage.fullName ?? ''}
                      className="minthr-avatar minthr-avatar-s minthr-ml-2"
                    />
                  )}
                </div>
              </div>
            </div>

            {model?.currency && (
              <div className="row g-0 minthr-flex-items-center minthr-mb-1">
                <div className="col-sm-4">
                  <span className="minthr-text-uppercase p">
                    {t('Currency')}:
                  </span>
                </div>
                <div className="col-sm-8">
                  <div className="minthr-color-dark h5">{model?.currency}</div>
                </div>
              </div>
            )}

            {model?.projectName && (
              <div className="row g-0 minthr-flex-items-center minthr-mb-1">
                <div className="col-sm-4">
                  <span className="minthr-text-uppercase p">
                    {t('ProjectName')}:
                  </span>
                </div>
                <div className="col-sm-8">
                  <div className="minthr-color-dark h5">
                    {model?.projectName}
                  </div>
                </div>
              </div>
            )}

            {model?.refundComment && model?.refundComment !== '' && (
              <>
                {model?.refundedBy && (
                  <div className="row g-0 minthr-flex-items-center minthr-mb-1">
                    <div className="col-sm-4">
                      <span className="minthr-text-uppercase p">
                        {t('Reimbursed.By')}
                      </span>
                    </div>
                    <div className="col-sm-8">
                      <div className="minthr-color-dark h5">
                        {t(model?.refundedBy)}
                      </div>
                    </div>
                  </div>
                )}
                <div className="row g-0 minthr-flex-items-center minthr-mb-1">
                  <div className="col-sm-4">
                    <span className="minthr-text-uppercase p">
                      {t('comment')}
                    </span>
                  </div>
                  <div className="col-sm-8">
                    <div className="minthr-color-dark h5">
                      {model?.refundComment}
                    </div>
                  </div>
                </div>
              </>
            )}

            {model?.description && (
              <div className="row g-0 minthr-flex-items-center minthr-mb-1">
                <div className="col-sm-12 minthr-mb-2">
                  <span className="minthr-text-uppercase p">
                    {t('Expenses.Remark')}
                  </span>
                </div>
                <div className="col-sm-12">
                  <div className="minthr-color-dark h5">
                    {model?.description}
                  </div>
                </div>
              </div>
            )}

            {model?.filesNames && model?.filesNames.length > 0 && (
              <div className="demande-actions-container">
                <div className="col-sm-12">
                  <div className="row g-0 minthr-flex-items-center">
                    <div className="col-sm-12">
                      <div className="demande-actions-container">
                        <div className="col-sm-12">
                          {model?.filesNames.map((file) => (
                            <div key={file.path} className="col-sm-12">
                              <div className="minthr-flex minthr-flex-items-center minthr-flex-space-between minthr-bg-light-gray minthr-text-decoration-none minthr-p-2 minthr-border-rounded minthr-mb-3">
                                <div className="minthr-flex minthr-flex-items-center minthr-w-80">
                                  <img
                                    className="minthr-icon-xl minthr-mr-2"
                                    src={getFileIcon(file.path ?? '')}
                                    alt="Document Icon"
                                  />
                                  <Tippy content={file.name}>
                                    <div className="minthr-w-100">
                                      <h6 className="minthr-text-truncate minthr-mb-0">
                                        {file.name}
                                      </h6>
                                    </div>
                                  </Tippy>
                                </div>
                                <div className="minthr-flex minthr-flex-items-center minthr-flex-nowrap">
                                  {isPreviewable(file.name ?? '') ? (
                                    <button
                                      rel="noopener noreferrer"
                                      className="minthr-p-0 minthr-border-none minthr-bg-transparent minthr-mr-2"
                                      onClick={() =>
                                        openMedia(
                                          `/upload/expensedoc/${file.path}`,
                                          file.path
                                            ?.split('.')
                                            .pop()
                                            ?.toLowerCase() ?? '',
                                          file.name ?? '',
                                        )
                                      }
                                    >
                                      <img
                                        className="minthr-icon-s"
                                        src="/icons/eye.svg"
                                        alt="Preview"
                                      />
                                    </button>
                                  ) : (
                                    <a
                                      href={`/upload/DownloadFile/${file.id}`}
                                      className="minthr-p-0 minthr-border-none minthr-bg-transparent minthr-mr-2"
                                      download
                                    >
                                      <img
                                        className="minthr-icon-s"
                                        src="/icons/download.svg"
                                        alt="Download"
                                      />
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {model?.status !== NoteStatus.Refund && (
              <>
                {model?.actionBy &&
                  model?.status !== NoteStatus.Draft &&
                  model?.status !== NoteStatus.Canceled && (
                    <p
                      className="description-header ot-tr-p-light mb-2"
                      style={{ textAlign: 'center' }}
                    >
                      {t('WaitingValidationBy')} {model?.actionBy}
                    </p>
                  )}

                {shouldShowStatusChangeButtons() && (
                  <div className="minthr-mt-5 minthr-flex-justify-center minthr-flex">
                    <button
                      onClick={() => {
                        if (model?.status === NoteStatus.Pending) {
                          handleStatusChange(NoteStatus.Success);
                        } else if (model?.nextStatus === 'Refund') {
                          openSidebar(ExpensesRefund, false, {
                            noteId: model?.id,
                            toggleRefresh: toggleRefresh,
                            toggleRefreshDT: params.toggleRefreshDT,
                            handleStatusChange: handleStatusChange,
                          });
                        } else {
                          handleStatusChange(NoteStatus.Pending);
                        }
                      }}
                      className={`minthr-btn minthr-btn-${getClassStatus(model)} minthr-mobile-ml-auto minthr-mobile-px-1 minthr-sidebar-open w-100 status-change`}
                    >
                      {getStatusText(model)}
                    </button>
                    {model?.status !== NoteStatus.Draft &&
                      model?.status !== NoteStatus.Canceled && (
                        <Tippy
                          placement="left"
                          content={t('Expenses.Cancel.Tooltip')}
                        >
                          <button
                            className="minthr-btn minthr-btn-gray minthr-ml-2 minthr-p-0 status-change"
                            onClick={() => {
                              openSidebar(ExpensesCanceled, false, {
                                noteId: model?.id,
                                toggleRefresh: toggleRefresh,
                                handleStatusChange: handleStatusChange,
                                toggleRefreshDT: params?.toggleRefreshDT,
                              });
                            }}
                          >
                            X
                          </button>
                        </Tippy>
                      )}
                  </div>
                )}
              </>
            )}

            {model?.history && model?.history.length > 0 && (
              <>
                <a
                  className="minthr-flex minthr-color-primary minthr-text-decoration-underline minthr-my-4 open-history"
                  onClick={toggleHistoryVisibility}
                >
                  {t('Expenses.see.historique')}
                  <img
                    className="minthr-icon-m minthr-ml-2"
                    src="/icons/back-in-time.svg"
                    alt=""
                  />
                </a>

                {isHistoryVisible && (
                  <Timeline
                    model={model}
                    history={model.history}
                    statusMap={statusMap}
                  />
                )}
              </>
            )}
          </div>

          <div className="minthr-border-t minthr-pt-4 minthr-mt-0">
            {shouldShowNewExpenseButton() && (
              <a
                className="minthr-btn minthr-btn-outline minthr-btn-primary minthr-sidebar-open"
                onClick={() => {
                  closeMultipleSidebars([
                    NewExpense as React.FC,
                    CategoryDetails as React.FC,
                    CategoryExpense as React.FC,
                  ]);
                  openSidebar(CategoryExpense, false, {
                    noteId: model?.id,
                    toggleRefresh: toggleRefresh,
                    toggleRefreshDT: params?.toggleRefreshDT,
                  });
                }}
                type="button"
              >
                <img
                  className="minthr-mr-1 minthr-mobile-mr-0"
                  src="/icons/plus.svg"
                  alt=""
                />
                {t('New.expense')}
              </a>
            )}
          </div>

          <div className="row g-0 minthr-flex-items-center minthr-mb-1 minthr-mt-2">
            <div className="col-sm-3">
              <span className="minthr-text-uppercase p">{t('Expenses')}</span>
            </div>
            <div className="col-sm-3">
              <span className="minthr-text-uppercase p">
                {model?.allExpenses && model?.allExpenses.length}
              </span>
            </div>
            <div className="col-sm-3">
              <span className="minthr-text-uppercase p">
                {t('Expenses.Total')}
              </span>
            </div>
            <div className="col-sm-3">
              <span className="minthr-text-uppercase p">
                {`${model?.totalExpenses?.toFixed(2).replace('.', ',') ?? '0,00'} ${model?.currency}`}
              </span>
            </div>
          </div>

          {model?.allExpenses &&
            model?.allExpenses.map((expense: ExpenseDetail) => (
              <div
                onClick={() => {
                  handleOpenSidebar(expense.id, expense?.expensesCat?.id);
                }}
                key={expense.id}
                className="minthr-flex minthr-flex-items-center minthr-border minthr-px-3 minthr-py-2 minthr-border-rounded minthr-mt-2 minthr-hover-secondary"
              >
                <div
                  className="minthr-icon-bg minthr-icon-bg-l minthr-icon-rounded minthr-mr-3"
                  style={{ background: expense?.expensesCat?.bgColor }}
                >
                  <i
                    className={expense?.expensesCat?.imgPath}
                    style={{ color: expense?.expensesCat?.imgColor }}
                  ></i>
                </div>
                <div>
                  <h4>
                    {expense.expensesCat?.name
                      ? t(expense.expensesCat.name)
                      : ''}
                  </h4>
                  <div className="h5 minthr-color-primary">{`${expense.infoAmount?.toLocaleString(user?.language || 'fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${model?.currency}`}</div>
                </div>
              </div>
            ))}
        </>
      )}
    </Sidebar>
  );
};

export default ExpenseDetails;
